import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'https://soldikeeper-backend-production.up.railway.app/api';

const api = axios.create({
  baseURL: `${API_BASE}/dms`,
  timeout: 30000,
});

api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  res => res.data,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.reload();
    }
    return Promise.reject(err.response?.data || err);
  }
);

export const dmsApi = {
  // Expose raw axios instance for custom calls
  axiosInstance: api,
  // Documents
  listDocuments: (params) => api.get('/documents', { params }),
  getDocument: (id) => api.get(`/documents/${id}`),
  uploadDocument: (formData) => api.post('/documents', formData, { timeout: 120000 }),
  updateDocument: (id, data) => api.put(`/documents/${id}`, data),
  publishDocument: (id, data = {}) => api.post(`/documents/${id}/publish`, data),
  deleteDocument: (id) => api.delete(`/documents/${id}`),
  downloadUrl: (id) => `${API_BASE}/dms/documents/${id}/download`,
  uploadVersion: (id, formData) => api.post(`/documents/${id}/versions`, formData, { timeout: 120000 }),
  getVersions: (id) => api.get(`/documents/${id}/versions`),

  // Comments
  getComments: (id) => api.get(`/documents/${id}/comments`),
  addComment: (id, data) => api.post(`/documents/${id}/comments`, data),
  resolveComment: (docId, commentId) => api.patch(`/documents/${docId}/comments/${commentId}/resolve`),

  // Permissions
  getPermissions: (id) => api.get(`/documents/${id}/permissions`),
  grantPermission: (id, data) => api.post(`/documents/${id}/permissions`, data),
  revokePermission: (id, permId) => api.delete(`/documents/${id}/permissions/${permId}`),

  // Folders
  listFolders: (params) => api.get('/folders', { params }),
  createFolder: (data) => api.post('/folders', data),
  updateFolder: (id, data) => api.put(`/folders/${id}`, data),
  deleteFolder: (id) => api.delete(`/folders/${id}`),

  // Search & Stats
  search: (params) => api.get('/search', { params }),
  getStats: () => api.get('/stats'),
  aiSearch: (query) => api.get('/ai/search', { params: { q: query } }),

  // Audit
  getAuditLogs: (params) => api.get('/audit', { params }),

  // AI Endpoints
  aiSummarize: (id) => api.post(`/documents/${id}/ai/summarize`),
  aiClassify: (id) => api.post(`/documents/${id}/ai/classify`),
  aiClassifyPreview: (data) => api.post('/ai/classify-preview', data),
  aiAsk: (id, question) => api.post(`/documents/${id}/ai/ask`, { question }),
  aiCompliance: (id) => api.post(`/documents/${id}/ai/compliance`),
  aiTranslate: (id, targetLang) => api.post(`/documents/${id}/ai/translate`, { targetLanguage: targetLang }),

  // Workflows
  initiateWorkflow: (docId, data) => api.post(`/documents/${docId}/workflows`, data),
  listWorkflows: (params) => api.get('/workflows', { params }),
  getWorkflow: (id) => api.get(`/workflows/${id}`),
  myPendingWorkflows: () => api.get('/workflows/mine'),
  actOnWorkflow: (id, data) => api.post(`/workflows/${id}/act`, data),
  cancelWorkflow: (id) => api.post(`/workflows/${id}/cancel`),

  // Analytics
  getAnalytics: (params) => api.get('/analytics', { params }),

  // Customer Portal
  listCustomerDocuments: (params) => api.get('/customer/documents', { params }),

  // Bulk Operations
  bulkOperation: (data) => api.post('/bulk', data),

  // Templates
  listTemplates: (params) => api.get('/templates', { params }),
  createFromTemplate: (templateId, data) => api.post(`/templates/${templateId}/create`, data),

  // Checkout / Checkin / Legal hold
  checkoutDocument: (id) => api.post(`/documents/${id}/checkout`),
  checkinDocument: (id) => api.post(`/documents/${id}/checkin`),
  setLegalHold: (id, hold) => api.put(`/documents/${id}/legal-hold`, { hold }),

  // AI Dashboard Insights
  getDashboardInsights: () => api.get('/ai/dashboard-insights'),
  // Duplicate Detection
  detectDuplicates: (data) => api.post('/ai/detect-duplicates', data),
  // Related Documents
  getRelatedDocuments: (id) => api.get(`/documents/${id}/related`),
  // Compliance Dashboard
  getComplianceDashboard: () => api.get('/compliance/dashboard'),

  // Agentic editor (Phases 1–4)
  agentEdit: (data) => api.post('/ai/agent-edit', data, { timeout: 120000 }),
  agentReview: (data) => api.post('/ai/agent-review', data, { timeout: 120000 }),
  agentSuggestions: (params) => api.get('/ai/agent-suggestions', {
    params: {
      mode: params.mode,
      content: typeof params.content === 'string' ? params.content : JSON.stringify(params.content),
      selection: params.selection ? JSON.stringify(params.selection) : undefined,
      documentId: params.documentId,
      chapterId: params.chapterId,
    },
    timeout: 60000,
  }),
  agentSync: (data) => api.post('/ai/agent-sync', data, { timeout: 120000 }),

  // Structured document editor (all users)
  saveEditorDocument: (data) => api.post('/editor/save', data),

  // Manual editor persistence (admin only)
  saveManualToDms: (data) => api.post('/manual/save', data),

  // Notifications
  notifications: {
    list: (unreadOnly = false, limit = 20) =>
      api.get('/notifications', { params: { unreadOnly, limit } }),
    markRead: (id) => api.put(`/notifications/${id}/read`),
    markAllRead: () => api.put('/notifications/read-all'),
    delete: (id) => api.delete(`/notifications/${id}`),
  },
};
